import { useMemo, useState } from "react"
import { motion } from "motion/react"
import {
	IconMediaPlayOutline18 as PlayIcon,
	IconXmarkOutline18 as XIcon,
} from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/cn"

import type { Attachment } from "../../hooks/useAttachments"
import { useFileIcon } from "../../hooks/useFileIcon"
import { IconButton } from "../icon-button"
import { VideoLightbox } from "../image-viewer/video-lightbox"
import { Loader } from "../loader"
import { Tooltip } from "../tooltip"

export function AttachmentPreview({
	attachment,
	onRemove,
	onImageClick,
	enableViewer = false,
	showNameLabel = false,
}: {
	attachment: Attachment
	onRemove: (id: string) => void
	onImageClick?: (originRect: DOMRect) => void
	enableViewer?: boolean
	showNameLabel?: boolean
}) {
	const { type, name, url, state } = attachment

	const isLoading = state === "loading"

	// We need it if reviving video thumbnails
	// const isDataUrl = url?.startsWith("data:")

	const {
		icon: fileIcon,
		isImageFile,
		isVideoFile,
	} = useFileIcon(type, name, "size-5")

	const isMediaFile = isImageFile || isVideoFile
	const [videoOpen, setVideoOpen] = useState(false)

	const handleDownload = async () => {
		if (!url) return
		try {
			const res = await fetch(url)
			const blob = await res.blob()
			const blobUrl = URL.createObjectURL(blob)
			const link = document.createElement("a")
			link.href = blobUrl
			link.download = name
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(blobUrl)
		} catch {
			window.open(url, "_blank", "noopener,noreferrer")
		}
	}

	const handleActivate = (e: React.MouseEvent) => {
		if (!enableViewer || isLoading || !url) return
		if (isImageFile) {
			const img = (e.currentTarget as HTMLElement).querySelector("img")
			if (img) onImageClick?.(img.getBoundingClientRect())
			return
		}
		if (isVideoFile) {
			setVideoOpen(true)
			return
		}
		void handleDownload()
	}

	const content = useMemo(() => {
		if (isImageFile) {
			return isLoading ? (
				<div className="size-10" />
			) : (
				<img
					src={url ?? ""}
					alt={name}
					className="size-10 rounded-lg object-cover shadow"
				/>
			)
		}

		if (isVideoFile && !isLoading && url) {
			return (
				<div className="relative size-10 overflow-hidden rounded-lg shadow">
					<video
						src={url}
						preload="metadata"
						muted
						className="size-10 object-cover"
					/>
					<div className="absolute inset-0 flex items-center justify-center bg-black/20">
						<div className="flex size-5 items-center justify-center rounded-full bg-black/60 text-white">
							<PlayIcon className="ml-px size-2.5 fill-current" />
						</div>
					</div>
				</div>
			)
		}

		return (
			<div
				className={cn(
					"relative flex size-10 items-center justify-center p-1.5 text-gray-600 transition-all dark:text-white",
					{
						"opacity-20": isLoading,
					},
				)}
			>
				{fileIcon}
			</div>
		)
	}, [isImageFile, isVideoFile, fileIcon, url, name, isLoading])

	return (
		<Tooltip label={name}>
			<motion.div
				layout
				layoutId={attachment.id}
				key={attachment.id}
				className="group/attachment relative rounded-lg ring-1 ring-gray-900/20 dark:ring-white/10"
				initial={{ transform: "scale(0.9)", opacity: 0 }}
				animate={{ transform: "scale(1)", opacity: 1 }}
				transition={{ duration: 0.15 }}
			>
				{/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
				<div
					aria-label={showNameLabel ? name : undefined}
					title={showNameLabel ? name : undefined}
					className={cn("w-auto rounded-lg object-cover", {
						"cursor-pointer": enableViewer && !isLoading,
					})}
					onClick={handleActivate}
				>
					{isLoading && (
						<div
							className={cn(
								"absolute inset-0 z-1 flex items-center justify-center rounded-lg",
								{ "opacity-80 dark:opacity-80": !isMediaFile },
							)}
						>
							<Loader className="size-5" />
						</div>
					)}
					{content}
				</div>

				<IconButton
					Icon={XIcon}
					size="sm"
					variant="subtle"
					className="absolute -right-2 -top-2 z-1 opacity-0 transition-opacity group-hover/attachment:opacity-100"
					onClick={() => onRemove(attachment.id)}
				/>
				{videoOpen && url && (
					<VideoLightbox src={url} onClose={() => setVideoOpen(false)} />
				)}
			</motion.div>
		</Tooltip>
	)
}
